import { useQuery } from "@tanstack/react-query";
import { listPublicWorkPhotos, type WorkPhotoRow } from "@/lib/work-photos.functions";
import { workCategories, workItems, type WorkCategory, type WorkItem } from "@/lib/work-gallery";

const validCategories = new Set<string>(workCategories.map((c) => c.id));

export function rowsToWorkItems(rows: WorkPhotoRow[]): WorkItem[] {
  return rows.flatMap<WorkItem>((row) => {
    if (!validCategories.has(row.category)) return [];
    const category = row.category as WorkCategory;
    if (row.kind === "before-after" || row.kind === "before-after-video") {
      if (!row.before_src || !row.after_src) return [];
      return [{
        id: row.id,
        type: row.kind === "before-after-video" ? "before-after-video" : "before-after",
        category,
        before: row.before_src,
        after: row.after_src,
        alt: row.alt || "Cleaning work",
      }];
    }
    if (!row.src) return [];
    return [{ id: row.id, type: row.kind === "video" ? "video" : "photo", category, src: row.src, alt: row.alt || "Cleaning work" }];
  });
}

export function useWorkItems() {
  const { data } = useQuery({
    queryKey: ["work-photos"],
    queryFn: () => listPublicWorkPhotos(),
    staleTime: 60_000,
  });

  if (!data) return workItems;
  const uploaded = rowsToWorkItems(data);
  if (uploaded.length === 0) return workItems;
  // Merge built-in photos with uploaded ones so a new upload never hides the
  // existing gallery. Rows already seeded from the built-in set share the
  // same category/type/description, so those built-ins are skipped to avoid
  // showing the same photo twice.
  const uploadedKeys = new Set(uploaded.map(itemKey));
  const builtIns = workItems.filter((i) => !uploadedKeys.has(itemKey(i)));
  return [...builtIns, ...uploaded];
}

function itemKey(i: WorkItem): string {
  return `${i.category}|${i.type}|${i.alt.trim().toLowerCase()}`;
}
